import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";
import axios from "../utils/axios";
import toast from "react-hot-toast";
import UniversalDashboard from "../components/UniversalDashboard";

const TelecallerDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [callQueue, setCallQueue] = useState([]);
  const [stats, setStats] = useState({
    totalCalls: 0,
    completedCalls: 0,
    connectedCalls: 0,
    followUps: 0,
    conversionRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeCall, setActiveCall] = useState(null);
  const [callNotes, setCallNotes] = useState("");

  const fetchCallData = async () => {
    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const [queueRes, statsRes] = await Promise.all([
        axios.get("/api/leads", {
          params: { assigned_to: "me", status: "new,contacted", _t: timestamp },
        }),
        axios.get("/api/analytics/telecaller-stats", {
          params: { _t: timestamp },
        }),
      ]);

      setCallQueue(queueRes.data.leads || []);
      setStats(statsRes.data || stats);
    } catch (error) {
      console.error("Error fetching call data:", error);
      toast.error("Failed to load call data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data whenever we navigate to this page
  useEffect(() => {
    setLoading(true);
    fetchCallData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-fetch when pathname changes

  const startCall = (lead) => {
    setActiveCall(lead);
    setCallNotes("");
    // In a real app, this would integrate with a calling system
    window.open(`tel:${lead.phone}`);
  };

  const endCall = async (outcome) => {
    if (!activeCall) return;

    try {
      const callData = {
        lead_id: activeCall.id,
        outcome: outcome,
        notes: callNotes,
        duration: Math.floor(Math.random() * 300) + 30, // Mock duration
        called_at: new Date().toISOString(),
      };

      await axios.post("/api/communications", {
        lead_id: activeCall.id,
        type: "call",
        content: `Call completed with outcome: ${outcome}. Notes: ${callNotes}`,
        status: "completed",
      });

      // Update lead status based on outcome
      let newStatus = activeCall.status;
      if (outcome === "interested") newStatus = "qualified";
      else if (outcome === "not_interested") newStatus = "lost";
      else if (outcome === "callback") newStatus = "contacted";

      await axios.put(`/api/leads/${activeCall.id}`, {
        status: newStatus,
        notes: callNotes,
      });

      toast.success("Call completed and logged successfully");
      setActiveCall(null);
      setCallNotes("");
      fetchCallData();
    } catch (error) {
      console.error("Error logging call:", error);
      toast.error("Failed to log call");
    }
  };

  // Call Queue Overview Component
  const CallQueueOverview = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <div className="loading-dots">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      );
    }

    return (
      <div>
        {/* Active Call Section */}
        {activeCall && (
          <div className="bg-white shadow rounded-lg mb-8 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Active Call
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900">Lead Information</h4>
                <div className="mt-2 space-y-1">
                  <p>
                    <span className="font-medium">Name:</span> {activeCall.name}
                  </p>
                  <p>
                    <span className="font-medium">Phone:</span>{" "}
                    {activeCall.phone}
                  </p>
                  <p>
                    <span className="font-medium">Email:</span>{" "}
                    {activeCall.email}
                  </p>
                  <p>
                    <span className="font-medium">Source:</span>{" "}
                    {activeCall.source || "Direct"}
                  </p>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Call Notes</h4>
                <textarea
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  rows="4"
                  placeholder="Enter call notes..."
                />
                <div className="mt-4 space-x-2">
                  <button
                    onClick={() => endCall("interested")}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    Interested
                  </button>
                  <button
                    onClick={() => endCall("not_interested")}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                  >
                    Not Interested
                  </button>
                  <button
                    onClick={() => endCall("callback")}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
                  >
                    Callback Later
                  </button>
                  <button
                    onClick={() => endCall("no_answer")}
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                  >
                    No Answer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call Queue */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Call Queue
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {callQueue.length > 0 ? (
                    callQueue.map((lead, index) => (
                      <tr key={lead.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              index < 3
                                ? "bg-red-100 text-red-800"
                                : index < 6
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {index < 3 ? "High" : index < 6 ? "Medium" : "Low"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {lead.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {lead.source || "Direct"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {lead.phone}
                          </div>
                          <div className="text-sm text-gray-500">
                            {lead.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              lead.status === "new"
                                ? "bg-blue-100 text-blue-800"
                                : lead.status === "contacted"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {lead.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {lead.updated_at
                            ? new Date(lead.updated_at).toLocaleDateString()
                            : "Never"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => startCall(lead)}
                            disabled={activeCall !== null}
                            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            Call Now
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-4 text-center text-sm text-gray-500"
                      >
                        No leads in call queue
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Stats Cards Component
  const statsCards = (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-blue-100 text-blue-600">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Total Calls</p>
            <p className="text-2xl font-semibold text-gray-900">
              {stats.totalCalls}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-green-100 text-green-600">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Completed</p>
            <p className="text-2xl font-semibold text-gray-900">
              {stats.completedCalls}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Connected</p>
            <p className="text-2xl font-semibold text-gray-900">
              {stats.connectedCalls}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-purple-100 text-purple-600">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Follow-ups</p>
            <p className="text-2xl font-semibold text-gray-900">
              {stats.followUps}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-indigo-100 text-indigo-600">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Conversion</p>
            <p className="text-2xl font-semibold text-gray-900">
              {stats.conversionRate}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <UniversalDashboard
      dashboardType="telecaller"
      title="Telecaller Dashboard"
      description={`Welcome back, ${user?.name}! Here's your call queue and performance.`}
      defaultComponent={CallQueueOverview}
      statsCards={statsCards}
    />
  );
};

export default TelecallerDashboard;
